import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { chatsApi } from "@/api/chats.ts";
import { cn } from "@/lib/cn.ts";
import { Alert, Spinner } from "@/ui/primitives.tsx";
import { useApiError } from "@/ui/useApiError.ts";
import { MemoryEditor } from "./MemoryEditor.tsx";
import { MemoryPanel } from "./MemoryPanel.tsx";

type Tab = "user" | "own" | "space" | "old";

/**
 * Chat section memory, in layers: the user's own (read by all of their conversations), the open conversation's (or
 * the one it shares), its space's, and the old shared file that conversations no longer read. Projects keep their
 * one project memory (MemoryPanel).
 */
export function ChatMemoryPanel({ chatId, projectId, canEdit }: { chatId: string; projectId: string; canEdit: boolean }) {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const qc = useQueryClient();
  const key = ["chat-memory", chatId];
  const q = useQuery({ queryKey: key, queryFn: () => chatsApi.memory(chatId) });
  const [tab, setTab] = useState<Tab>("own");
  const save = useMutation({ mutationFn: ({ scope, content }: { scope: "own" | "space" | "user"; content: string }) => chatsApi.saveMemory(chatId, scope, content), onSuccess: (d) => qc.setQueryData(key, d) });
  const toggle = useMutation({ mutationFn: (on: boolean) => chatsApi.update(chatId, { useMemory: on }), onSuccess: () => void qc.invalidateQueries({ queryKey: key }) });
  if (q.isLoading) return <div className="p-3"><Spinner /></div>;
  if (q.error) return <div className="p-3"><Alert tone="danger">{toMsg(q.error)}</Alert></div>;
  const m = q.data!;
  const tabs: Tab[] = m.space ? ["user", "own", "space", "old"] : ["user", "own", "old"];
  const note = (text: string, path?: string) => (
    <p className="border-b border-border px-3 py-2 text-xs text-fg-muted">{text}{path && <> <code dir="ltr" className="rounded bg-surface-2 px-1">{path}</code></>}</p>
  );
  return (
    <div className="flex h-full flex-col text-sm">
      <div className="space-y-2 border-b border-border p-3">
        <div role="tablist" className="flex gap-1 rounded-lg bg-surface-2 p-0.5">
          {tabs.map((k) => (
            <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} className={cn("min-w-0 flex-1 truncate rounded-md px-2 py-1 text-xs font-medium", tab === k ? "bg-surface text-fg shadow-sm" : "text-fg-muted hover:text-fg")}>
              {k === "space" ? t("chatMemory.tabs.space", { name: m.space!.name }) : t(`chatMemory.tabs.${k}`)}
            </button>
          ))}
        </div>
        {tab !== "old" && (
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={m.useMemory} disabled={!canEdit || toggle.isPending} onChange={(e) => toggle.mutate(e.target.checked)} /> {t("chatMemory.useMemory")}
          </label>
        )}
        {(save.error || toggle.error) && <Alert tone="danger">{toMsg(save.error ?? toggle.error)}</Alert>}
      </div>
      {tab === "user" && (
        <MemoryEditor key={`user:${m.user.path}`} content={m.user.content} canEdit={canEdit} saving={save.isPending} onSave={(content) => save.mutate({ scope: "user", content })} header={note(t("chatMemory.userHint"), m.user.path)} />
      )}
      {tab === "own" && (
        <MemoryEditor
          key={`own:${m.own.path}`}
          content={m.own.content}
          canEdit={canEdit}
          saving={save.isPending}
          onSave={(content) => save.mutate({ scope: "own", content })}
          header={note(m.own.sharedWith ? t("chatMemory.sharedHint", { title: m.own.sharedWith.title }) : t("chatMemory.ownHint"), m.own.path)}
        />
      )}
      {tab === "space" && m.space && (
        <MemoryEditor key={`space:${m.space.path}`} content={m.space.content} canEdit={canEdit} saving={save.isPending} onSave={(content) => save.mutate({ scope: "space", content })} header={note(t("chatMemory.spaceHint", { name: m.space.name }), m.space.path)} />
      )}
      {tab === "old" && <MemoryPanel projectId={projectId} canEdit={canEdit} chatSection />}
    </div>
  );
}
